/**
 * The league view's day picker (SCORES-15/Q73, Scores UX spec v1.0 section
 * 4.2): a horizontally scrolling strip carrying only days that actually have
 * games, in a fixed 64px row.
 *
 * Each chip is a FIXED 48x56 box (`dayChipWidth` x `dayChipHeight`) centered
 * in the row, so it can never stretch into a tall pill. Line 1 is the weekday
 * ("Mon"), "Today" for today, or the month ("Nov") when the month changes
 * from the chip before; line 2 is the day number. The selected chip is a
 * `primarySolid` fill with `onPrimary` text. Every chip's accessible name is
 * the human day ("Monday, Oct 19, today, selected"), never the ISO string.
 *
 * On mount the strip jumps so the selected chip sits second from the left;
 * picking a day glides it there, or jumps under reduced motion.
 *
 * NOT a tablist, and it must not become one again. It has no tabpanel, and
 * picking a day writes a URL search param rather than swapping a panel. Under
 * `role="tab"` Chrome also drives the selected state off focus, so the chosen
 * chip reported `selected: false` the moment focus moved. ISSUE-957.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef } from "react";

import {
  dayChipTopLabel,
  dayOfMonth,
  isToday,
  longDayLabel,
} from "./calendarDay";
import { DAY_STRIP_LABEL } from "./leagueSelectorContract";
import { scrollStripToChip } from "./stripScroll";

export type typeDateStripProps = {
  /** Only days that have games, ascending. The selected day is rendered as
   *  well, in sorted position, when it is not in this list. */
  days: readonly string[];
  selectedDay: string | null;
  /** Today in the fan's zone, "YYYY-MM-DD": marks the Today chip. */
  todayDay: string;
  onSelectDay: (day: string) => void;
};

/** A strip chip's accessible name: "Monday, Oct 19, today, selected". */
export function stripChipLabel(input: {
  label: string;
  isToday: boolean;
  isSelected: boolean;
}): string {
  return [
    input.label,
    input.isToday ? "today" : null,
    input.isSelected ? "selected" : null,
  ]
    .filter((part) => part !== null)
    .join(", ");
}

/** The fixed strip row and the scroller inside it, shared with WeekStrip. */
export const STRIP_SCROLLER_CLASS =
  "relative flex h-day-strip-height items-center gap-1 overflow-x-auto ps-1 pe-1 [scrollbar-width:none]";

/** One strip chip: never stretched, never shrunk, filled when selected. */
export function stripChipClassName(isSelected: boolean): string {
  return cn(
    "flex h-day-chip-height shrink-0 flex-col items-center justify-center self-center rounded-md tabular-nums",
    "transition-colors duration-200 motion-reduce:transition-none",
    isSelected
      ? "bg-primary-solid text-on-primary"
      : "bg-transparent text-ink hover:bg-surface-sunken",
    FOCUS_RING,
  );
}

export const DateStrip = (props: typeDateStripProps): ReactNode => {
  const stripRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const renderedDays =
    props.selectedDay !== null && !props.days.includes(props.selectedDay)
      ? [...props.days, props.selectedDay].sort()
      : props.days;
  const dayKey = renderedDays.join(",");
  useEffect(() => {
    const selected = props.selectedDay;
    const strip = stripRef.current;
    if (selected === null || strip === null) return;
    const chip = [...strip.children].find(
      (child) => child.getAttribute("data-day") === selected,
    );
    if (!(chip instanceof HTMLElement)) return;
    scrollStripToChip({ strip, chip, isInitial: !hasScrolledRef.current });
    hasScrolledRef.current = true;
  }, [props.selectedDay, dayKey]);

  if (renderedDays.length === 0) {
    return null;
  }

  return (
    <div
      ref={stripRef}
      role="group"
      aria-label={DAY_STRIP_LABEL}
      className={STRIP_SCROLLER_CLASS}
      data-testid="date-strip"
    >
      {renderedDays.map((day, index) => {
        const isSelected = day === props.selectedDay;
        const isTodayChip = isToday(day, props.todayDay);
        return (
          <button
            key={day}
            type="button"
            className={cn("w-day-chip-width", stripChipClassName(isSelected))}
            data-testid="date-strip-day"
            data-day={day}
            data-has-games={props.days.includes(day) ? "true" : "false"}
            data-today={isTodayChip ? "true" : undefined}
            aria-current={isSelected ? "date" : undefined}
            aria-label={stripChipLabel({
              label: longDayLabel(day, props.todayDay),
              isToday: isTodayChip,
              isSelected,
            })}
            onClick={() => {
              props.onSelectDay(day);
            }}
          >
            <span
              className={cn(
                "text-small",
                isSelected
                  ? "text-on-primary"
                  : isTodayChip
                    ? "text-primary"
                    : "text-ink-secondary",
              )}
              data-testid="date-strip-day-top"
            >
              {dayChipTopLabel({
                day,
                todayDay: props.todayDay,
                previousDay: renderedDays[index - 1] ?? null,
              })}
            </span>
            <span
              className="text-body-strong"
              data-testid="date-strip-day-number"
            >
              {dayOfMonth(day)}
            </span>
          </button>
        );
      })}
    </div>
  );
};
